import { useAddStudentToClassMutation } from 'util/rtk-api'

import { DoneButton } from 'components/blue-button'
import DismissButton from 'components/dismiss-button'
import Dropdown, { Selector } from 'components/selector'
import UploadBox from 'components/upload-box'
import { useClassContext } from 'contexts/class-context'
import styles from 'css/components/classroom/add-new-student-overlay.module.css'
import { useState, useCallback, useContext } from 'react'

export const AddNewStudentOverlay = ({ setShowAddNewStudentOverlay, className }) => {
    const { classId, students, addStudent } = useClassContext();
    const [isLoading, setIsLoading] = useState(false)
    const [addStudentToClass] = useAddStudentToClassMutation()

    // Add state for form inputs
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [difficulty, setDifficulty] = useState('Not Assigned')
    const [schoolStudentId, setSchoolStudentId] = useState('')
    const [grade, setGrade] = useState('')

    const onBlur = ({target}) => {
        target.value = target.value.trim()
        if (target.value.match(/./)) {
            target.setCustomValidity('')
            return
        }
        target.setCustomValidity('Please enter a valid value')
        // target.reportValidity()
    }

    // Require a first and last name (each may be a single character) so we never
    // send an empty Cognito username. Whitespace-only names don't count.
    const canSubmit = firstName.trim() !== '' && lastName.trim() !== ''

    const handleDone = async () => {
        if (!canSubmit || isLoading) return

        setIsLoading(true)
        const result = await addStudentToClass({
            classId: classId,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            grade: grade,
            schoolStudentId: schoolStudentId,
            difficulty
        })
        setIsLoading(false)

        if (result.data) {
            addStudent(result.data)
            setShowAddNewStudentOverlay(false)
        }
        // On error, leave the overlay open so the user can correct and retry.
    }

    return (
        <div className="overlay">
            <div className="overlay-content-container">
                <div className="overlay-content-header">
                    <h3>Add New Student</h3>
                    <DismissButton onClick={() => setShowAddNewStudentOverlay(false)} />
                </div>
                <div className={`overlay-content-body`}>
                    <div className={styles.studentContainer}>
                        <div className={styles.row}>
                            Class: <span className={styles.boldText}>{className}</span>
                        </div>
                        <div className={styles.row}>
                            <div>
                                First Name
                                <input
                                    type="text"
                                    defaultValue={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    onBlur={onBlur}
                                />
                            </div>
                            <div>
                                Last Name
                                <input
                                    type="text"
                                    defaultValue={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    onBlur={onBlur}
                                />
                            </div>
                        </div>
                        <div className={styles.row}>
                            <div>
                                School Student ID
                                <input
                                    type="text"
                                    defaultValue={schoolStudentId}
                                    onChange={(e) => setSchoolStudentId(e.target.value)}
                                    onBlur={onBlur}
                                />
                            </div>
                            <div>
                                <Selector
                                    options={['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']}
                                    onSelect={(value) => setGrade(value)}
                                    placeholder="Grade"
                                    value={grade}
                                />
                            </div>
                        </div>
                        <div className={styles.row}>
                            <div>
                                Student Level:
                                <div className={styles.row}>
                                    <label>
                                        <input
                                            type="radio"
                                            name="difficulty"
                                            value="Not Assigned"
                                            checked={difficulty === 'Not Assigned'}
                                            onChange={(e) => setDifficulty(e.target.value)}
                                        />
                                        Not Assigned
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="difficulty"
                                            value="Emerging"
                                            checked={difficulty === 'Emerging'}
                                            onChange={(e) => setDifficulty(e.target.value)}
                                        />
                                        Emerging
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="difficulty"
                                            value="Intermediate"
                                            checked={difficulty === 'Intermediate'}
                                            onChange={(e) => setDifficulty(e.target.value)}
                                        />
                                        Intermediate
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="difficulty"
                                            value="Advanced"
                                            checked={difficulty === 'Advanced'}
                                            onChange={(e) => setDifficulty(e.target.value)}
                                        />
                                        Advanced
                                    </label>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className={styles.alignRight}>
                        <DoneButton disabled={isLoading || !canSubmit} onClick={handleDone} />
                    </div>
                </div>
            </div>
        </div>
    )
}

// Debounce utility function
function debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout)
            func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
    }
}